import type { TripView } from '@voll-intelligence/domain';
import { calendarDateFromUtcDay, formatTripRange, tripName } from '@voll-intelligence/trip-panel';

import { t } from '@web/i18n';
import { Button } from '@web/ui/core/atoms';
import type { CardTripState } from '@web/ui/core/hooks';
import { TripItineraryWithVoucher } from '@web/ui/core/organisms';

/**
 * O corpo da viagem dentro de `CardSideColumn`: read-only (o operador vê o
 * itinerário e o voucher, não compõe pedido nem arquiva — por isso nem
 * `onCompose` nem `renderEmptied` chegam ao `TripItinerary`). Sem viagem
 * (`none`) não renderiza nada; a moldura (`aside`/`Drawer`) é do pai.
 */
export function CardTripColumn({
  state,
}: Readonly<{ state: CardTripState }>): React.JSX.Element | null {
  if (state.status === 'none') return null;
  return <TripColumnBody state={state} />;
}

function TripColumnBody({
  state,
}: Readonly<{ state: Exclude<CardTripState, { status: 'none' }> }>): React.JSX.Element {
  if (state.status === 'loading') {
    return (
      <p className="px-5 py-6 text-meta text-muted-foreground" data-testid="card-trip-loading">
        {t('card.trip.loading')}
      </p>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="flex flex-col items-start gap-3 px-5 py-6">
        <p className="text-meta text-tone-red-fg">{t('card.trip.loadError')}</p>
        <Button variant="outline" size="sm" onClick={state.retry}>
          {t('card.trip.retry')}
        </Button>
      </div>
    );
  }
  return <TripReady trip={state.trip} />;
}

function TripReady({ trip }: Readonly<{ trip: TripView }>): React.JSX.Element {
  // `tripName` não converte mais data nenhuma (é só o nome escrito ou a cidade do
  // destino): a janela mora SÓ aqui, na linha abaixo do h2. As datas são dia-só à
  // meia-noite UTC — sem esta conversão, um fuso negativo imprime a faixa um dia antes.
  const range = formatTripRange(
    calendarDateFromUtcDay(trip.startAt),
    calendarDateFromUtcDay(trip.endAt),
  );
  return (
    <>
      <header className="flex flex-col gap-0.5 border-b border-border-soft px-5 py-4">
        <h2 className="m-0 text-base font-medium text-foreground">{tripName(trip)}</h2>
        {range ? <p className="m-0 text-meta text-muted-foreground">{range}</p> : null}
      </header>
      <TripItineraryWithVoucher trip={trip} className="min-h-0 flex-1 overflow-auto px-5 py-4" />
    </>
  );
}
